/**
 * local-folder — a parent-served "Local Folder" knowledge source for Office
 * add-ins. The embed's own Local Folder needs Chromium's File System Access
 * pickers, which don't exist in WKWebView (macOS Office) and are spec-blocked
 * in cross-origin iframes — so the TASK PANE picks the folder (via the
 * universally supported `<input webkitdirectory>`), holds a read-only snapshot
 * in memory, and serves it to the model as parent tools.
 *
 * Host-agnostic and pure (no Office.js, no React): the shell wires the picker
 * UI and tool routing (see office-shell App.tsx). Prior art: usable-chat's
 * browser_folder__* provider (fragments a7ace6a8, 22f23c73) — deliberately
 * different tool names (local_folder_*) to avoid colliding with the
 * server-registered browser_folder tool definitions.
 */

import type { ParentToolSchema } from "./embed-sdk";

// ---------------------------------------------------------------------------
// Caps
// ---------------------------------------------------------------------------

/** Max entries kept from a folder snapshot (rest dropped, noted in summary). */
export const MAX_FOLDER_ENTRIES = 2000;
/** Files larger than this are listed but not readable. */
export const MAX_READABLE_FILE_BYTES = 2 * 1024 * 1024;
/** Text returned per read is capped at this many characters. */
export const MAX_READ_CHARS = 256 * 1024;
/** Default / max grep matches returned. */
export const DEFAULT_GREP_MATCHES = 50;
export const MAX_GREP_MATCHES = 200;

/** Extensions treated as text without sniffing. */
const TEXT_EXTENSIONS = new Set([
  "txt", "md", "markdown", "csv", "tsv", "json", "ndjson", "yaml", "yml", "xml",
  "html", "htm", "css", "js", "mjs", "cjs", "ts", "tsx", "jsx", "py", "rb", "go",
  "rs", "java", "kt", "c", "h", "cpp", "hpp", "cs", "sh", "bash", "zsh", "sql",
  "toml", "ini", "cfg", "conf", "env", "log", "tex", "rst", "adoc", "svg",
]);

/** Extensions that are always binary (skip without sniffing). */
const BINARY_EXTENSIONS = new Set([
  "png", "jpg", "jpeg", "gif", "webp", "ico", "bmp", "tiff", "heic",
  "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "key", "pages", "numbers",
  "zip", "gz", "tar", "7z", "rar", "dmg", "iso",
  "mp3", "mp4", "mov", "avi", "mkv", "wav", "flac",
  "woff", "woff2", "ttf", "otf", "eot",
  "exe", "dll", "so", "dylib", "bin", "duckdb", "sqlite", "db",
]);

/** Structured-data extensions that should go to data staging, not text reads. */
const TABULAR_EXTENSIONS = new Set(["csv", "tsv", "xlsx", "xls", "parquet"]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LocalFolderEntry {
  /** Folder-relative path (webkitRelativePath without the root segment). */
  path: string;
  name: string;
  size: number;
  type: string;
  file: File;
  /** Present when the file is listed but cannot be read as text. */
  skipped?: "too-large" | "binary";
}

export interface LocalFolderProvider {
  attach(files: FileList | File[]): { folderName: string; fileCount: number; dropped: number };
  detach(): void;
  isAttached(): boolean;
  /** Human/context-entry summary of the attached folder, or null. */
  summary(): string | null;
  folderName(): string | null;
  fileCount(): number;
  toolSchemas: ParentToolSchema[];
  canHandle(tool: string): boolean;
  handleToolCall(tool: string, args: Record<string, unknown>): Promise<unknown>;
}

// ---------------------------------------------------------------------------
// Helpers (pure)
// ---------------------------------------------------------------------------

async function blobText(blob: Blob): Promise<string> {
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("Failed to read file text."));
    reader.readAsText(blob);
  });
}

async function blobBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === "function") return new Uint8Array(await blob.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(new Error("Failed to read file bytes."));
    reader.readAsArrayBuffer(blob);
  });
}

function extensionOf(path: string): string {
  const dot = path.lastIndexOf(".");
  return dot === -1 ? "" : path.slice(dot + 1).toLowerCase();
}

/**
 * Normalize a picked FileList: derive the folder name from the common first
 * path segment of webkitRelativePath (plain multi-file picks have none — the
 * WKWebView fallback), classify readability, and apply the entry cap.
 */
export function normalizeFileList(files: FileList | File[]): {
  folderName: string;
  entries: LocalFolderEntry[];
  dropped: number;
} {
  const list = Array.from(files as ArrayLike<File>);
  let folderName = "attached-files";

  const entries: LocalFolderEntry[] = [];
  for (const file of list) {
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || "";
    let path = file.name;
    if (rel) {
      const segments = rel.split("/");
      if (segments.length > 1) {
        folderName = segments[0];
        path = segments.slice(1).join("/");
      }
    }
    const ext = extensionOf(path);
    let skipped: LocalFolderEntry["skipped"];
    if (file.size > MAX_READABLE_FILE_BYTES) skipped = "too-large";
    else if (BINARY_EXTENSIONS.has(ext)) skipped = "binary";
    entries.push({ path, name: file.name, size: file.size, type: file.type, file, skipped });
  }

  // Plain ASCII sort — localeCompare ordering varies by environment.
  entries.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  const dropped = Math.max(0, entries.length - MAX_FOLDER_ENTRIES);
  return { folderName, entries: entries.slice(0, MAX_FOLDER_ENTRIES), dropped };
}

/** Read a file as text with the NUL-byte binary sniff and the char cap. */
export async function readEntryText(entry: LocalFolderEntry): Promise<string> {
  if (entry.skipped === "too-large") {
    throw new Error(
      `"${entry.path}" is too large to read (${Math.round(entry.size / 1024)} KB > ${Math.round(
        MAX_READABLE_FILE_BYTES / 1024
      )} KB cap).`
    );
  }
  if (entry.skipped === "binary") {
    const ext = extensionOf(entry.path);
    const IMAGE_HINT_EXTS = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"]);
    const hint = TABULAR_EXTENSIONS.has(ext)
      ? " This is a structured data file — for analysis, ask the user to attach it via the chat paperclip (data staging) instead of reading it as text."
      : IMAGE_HINT_EXTS.has(ext)
        ? " This is an IMAGE — you can place it on slides directly: pass this path to useUploadedImage (compose content / insert_image) or branding.logoUpload."
        : "";
    throw new Error(`"${entry.path}" is a binary file (${ext || entry.type}) and cannot be read as text.${hint}`);
  }
  const ext = extensionOf(entry.path);
  if (!TEXT_EXTENSIONS.has(ext)) {
    // Unknown extension — sniff the first 512 bytes for NULs.
    const head = await blobBytes(entry.file.slice(0, 512));
    for (let i = 0; i < head.length; i++) {
      if (head[i] === 0) {
        entry.skipped = "binary";
        throw new Error(`"${entry.path}" looks binary (NUL bytes) and cannot be read as text.`);
      }
    }
  }
  const text = await blobText(entry.file);
  if (text.length > MAX_READ_CHARS) {
    return (
      text.slice(0, MAX_READ_CHARS) +
      `\n…[truncated — file is ${text.length} chars, showing the first ${MAX_READ_CHARS}]`
    );
  }
  return text;
}

/** Case-insensitive nearest-path suggestions for a missing path. */
export function nearestPaths(entries: LocalFolderEntry[], wanted: string, max = 3): string[] {
  const lower = wanted.toLowerCase();
  const base = lower.split("/").pop() ?? lower;
  return entries
    .filter((e) => e.path.toLowerCase().includes(base))
    .slice(0, max)
    .map((e) => e.path);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

// Description discipline (fragment 22f23c73): say the result returns before the
// next response; never narrate "request sent"; steer tabular files to staging.
const RESULT_NOTE =
  "The result is returned to you before your next response — use it directly; never tell the user a request was sent.";

const TOOL_SCHEMAS: ParentToolSchema[] = [
  {
    name: "local_folder_list",
    description:
      "List the files in the local folder the user attached from their computer (read-only " +
      "snapshot). Start here before reading. Paths are RELATIVE TO THE FOLDER ROOT — do not " +
      "prefix them with the folder's name. Returns paths with sizes and " +
      `readability. ${RESULT_NOTE}`,
    parameters: {
      type: "object",
      properties: {
        path: { type: "string", description: "Optional subtree prefix to list (folder-relative)." },
        maxEntries: { type: "number", description: "Cap the listing (default 200)." },
      },
    },
  },
  {
    name: "local_folder_read",
    description:
      "Read one text file from the user's attached local folder by its folder-relative path " +
      "(from local_folder_list). Returns up to 256 KB of text. Binary files are not readable; " +
      "for structured data files (.csv/.xlsx) prefer asking the user to attach them via the " +
      `chat paperclip (data staging) when analysis is needed. ${RESULT_NOTE}`,
    parameters: {
      type: "object",
      properties: {
        path: { type: "string", description: "Folder-relative path of the file to read." },
      },
      required: ["path"],
    },
  },
  {
    name: "local_folder_grep",
    description:
      "Search the text files of the user's attached local folder for a pattern (plain text or " +
      "regular expression). Returns matches as {path, line, text}, capped. Use to locate which " +
      `file mentions something before reading it. ${RESULT_NOTE}`,
    parameters: {
      type: "object",
      properties: {
        pattern: { type: "string", description: "Text or JavaScript regex source to search for." },
        pathPrefix: { type: "string", description: "Optional folder-relative subtree to search." },
        maxMatches: { type: "number", description: `Max matches (default ${DEFAULT_GREP_MATCHES}, cap ${MAX_GREP_MATCHES}).` },
      },
      required: ["pattern"],
    },
  },
];

// Module-level pointer to the currently attached folder's summary so OTHER
// file-discovery tools (e.g. PowerPoint's list_uploaded_files) can signpost it
// — the model often reaches for the wrong discovery tool first.
let activeFolderNotice: string | null = null;
// Live entries of the attached folder so image/logo sources can resolve folder
// files (e.g. Brand/logo.png) without going through the text-only read tool.
let activeEntries: LocalFolderEntry[] | null = null;
let activeFolderName: string | null = null;

/** Notice for cross-signposting from other file tools, or null. */
export function getActiveLocalFolderNotice(): string | null {
  return activeFolderNotice;
}

/** Images from the attached folder may be larger than the text-read cap. */
export const MAX_FOLDER_IMAGE_BYTES = 10 * 1024 * 1024;

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"]);

/**
 * Resolve a file from the ACTIVE attached folder to a data URL (for slide
 * image/logo sources). Matches by folder-relative path or basename,
 * case-insensitive, tolerating the folder name as a leading segment. Returns
 * null when no folder is attached or nothing matches — callers fall through
 * to their other sources.
 */
export async function readActiveFolderFileAsDataUrl(nameOrPath: string): Promise<string | null> {
  if (!activeEntries) return null;
  let wanted = nameOrPath.replace(/^\/+/, "").toLowerCase();
  if (activeFolderName) {
    const folderLower = activeFolderName.toLowerCase();
    if (wanted.startsWith(folderLower + "/")) wanted = wanted.slice(folderLower.length + 1);
  }
  const base = wanted.split("/").pop() ?? wanted;
  const entry =
    activeEntries.find((e) => e.path.toLowerCase() === wanted) ??
    activeEntries.find((e) => e.name.toLowerCase() === base);
  if (!entry) return null;
  if (entry.size > MAX_FOLDER_IMAGE_BYTES) {
    throw new Error(
      `"${entry.path}" is too large to use as an image (${Math.round(entry.size / 1024 / 1024)} MB > 10 MB).`
    );
  }
  const ext = extensionOf(entry.path);
  if (!IMAGE_EXTENSIONS.has(ext) && !entry.type.startsWith("image/")) {
    throw new Error(`"${entry.path}" is not an image file.`);
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error(`Failed to read "${entry.path}".`));
    reader.readAsDataURL(entry.file);
  });
}

export function createLocalFolderProvider(): LocalFolderProvider {
  let entries: LocalFolderEntry[] = [];
  let name: string | null = null;
  let droppedCount = 0;

  const requireAttached = (): LocalFolderEntry[] => {
    if (!name) {
      throw new Error("No local folder is attached. Ask the user to attach one via the add-in's folder button.");
    }
    return entries;
  };

  // Paths are folder-relative (the root segment is stripped at attach), but
  // the context entry names the folder — so models naturally pass
  // "Brand/logo.png" or path: "Brand". Tolerate that by stripping the folder
  // name when it appears as the leading segment (found live: list {path:
  // "<folderName>"} returned an empty listing).
  const normalizeQueryPath = (input: string): string => {
    let path = input.replace(/^\/+/, "");
    if (name) {
      const lower = path.toLowerCase();
      const folderLower = name.toLowerCase();
      if (lower === folderLower) return "";
      if (lower.startsWith(folderLower + "/")) return path.slice(name.length + 1);
    }
    return path;
  };

  return {
    attach(files) {
      const normalized = normalizeFileList(files);
      entries = normalized.entries;
      name = normalized.folderName;
      droppedCount = normalized.dropped;
      activeFolderNotice =
        `A local folder "${name}" (${entries.length} files) is attached to this conversation — ` +
        `explore it with local_folder_list / local_folder_read / local_folder_grep. ` +
        `Image files in it can be used directly on slides via useUploadedImage / branding.logoUpload (pass the path).`;
      activeEntries = entries;
      activeFolderName = name;
      return { folderName: name, fileCount: entries.length, dropped: droppedCount };
    },

    detach() {
      entries = [];
      name = null;
      droppedCount = 0;
      activeFolderNotice = null;
      activeEntries = null;
      activeFolderName = null;
    },

    isAttached: () => name !== null,
    folderName: () => name,
    fileCount: () => entries.length,

    summary() {
      if (!name) return null;
      const readable = entries.filter((e) => !e.skipped).length;
      return (
        `The user attached the local folder "${name}" to this conversation (read-only snapshot, ` +
        `${entries.length} files, ${readable} readable as text` +
        (droppedCount > 0 ? `, ${droppedCount} dropped over the ${MAX_FOLDER_ENTRIES}-entry cap` : "") +
        `). Explore it with local_folder_list, local_folder_read, and local_folder_grep.`
      );
    },

    toolSchemas: TOOL_SCHEMAS,

    canHandle: (tool) => TOOL_SCHEMAS.some((t) => t.name === tool),

    async handleToolCall(tool, args) {
      const all = requireAttached();
      switch (tool) {
        case "local_folder_list": {
          const prefix = typeof args.path === "string" ? normalizeQueryPath(args.path as string) : "";
          const cap = Math.min(typeof args.maxEntries === "number" ? (args.maxEntries as number) : 200, 1000);
          const matching = prefix ? all.filter((e) => e.path.startsWith(prefix)) : all;
          return {
            folder: name,
            totalFiles: matching.length,
            truncated: matching.length > cap,
            files: matching.slice(0, cap).map((e) => ({
              path: e.path,
              sizeBytes: e.size,
              readable: !e.skipped,
              ...(e.skipped ? { skipped: e.skipped } : {}),
            })),
          };
        }

        case "local_folder_read": {
          const path = normalizeQueryPath(String(args.path ?? ""));
          const entry = all.find((e) => e.path === path) ?? all.find((e) => e.path.toLowerCase() === path.toLowerCase());
          if (!entry) {
            const suggestions = nearestPaths(all, path);
            throw new Error(
              `No file "${path}" in the attached folder.` +
                (suggestions.length ? ` Did you mean: ${suggestions.join(", ")}?` : " Use local_folder_list first.")
            );
          }
          const content = await readEntryText(entry);
          return { path: entry.path, sizeBytes: entry.size, content };
        }

        case "local_folder_grep": {
          const rawPattern = String(args.pattern ?? "");
          if (!rawPattern) throw new Error("pattern is required.");
          let regex: RegExp;
          try {
            regex = new RegExp(rawPattern, "i");
          } catch {
            regex = new RegExp(rawPattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
          }
          const prefix = typeof args.pathPrefix === "string" ? normalizeQueryPath(args.pathPrefix as string) : "";
          const cap = Math.min(
            typeof args.maxMatches === "number" ? (args.maxMatches as number) : DEFAULT_GREP_MATCHES,
            MAX_GREP_MATCHES
          );
          const matches: Array<{ path: string; line: number; text: string }> = [];
          let scanned = 0;
          for (const entry of all) {
            if (matches.length >= cap) break;
            if (entry.skipped) continue;
            if (prefix && !entry.path.startsWith(prefix)) continue;
            let text: string;
            try {
              text = await readEntryText(entry);
            } catch {
              continue; // sniffed binary mid-scan — skip
            }
            scanned++;
            const lines = text.split("\n");
            let perFile = 0;
            for (let i = 0; i < lines.length && matches.length < cap && perFile < 10; i++) {
              if (regex.test(lines[i])) {
                matches.push({ path: entry.path, line: i + 1, text: lines[i].slice(0, 300) });
                perFile++;
              }
            }
          }
          return { folder: name, pattern: rawPattern, filesScanned: scanned, truncated: matches.length >= cap, matches };
        }

        default:
          throw new Error(`Unknown local-folder tool: "${tool}"`);
      }
    },
  };
}
